import {
  Component,
  Input,
  Output,
  EventEmitter,
  ViewChild,
  ElementRef,
  OnInit,
  OnChanges,
} from '@angular/core';

export interface TabMenuState {
  isOpen: boolean;
  top: string;
  left: string;
  item: Tab<any> | null;
}

@Component({
    selector: 'app-tabs',
    styleUrls: ['./tabs.component.scss'],
    templateUrl: './tabs.component.html',
    standalone: false
})
export class TabsComponent implements OnInit, OnChanges {
  @Input() tabs: Tab<any>[] = [];
  @Input() showAddNewTab: boolean = false;
  @Input() processingNewTab: boolean = false;
  /** When true, clicking a tab does not change selection (e.g. during save/create). */
  @Input() disableTabSelection: boolean = false;
  @Output() newTab = new EventEmitter();
  @Output() selectedTab = new EventEmitter();
  @Output() removeTab = new EventEmitter();
  @Output() menuItemClicked = new EventEmitter();

  @ViewChild('tabHeader') tabHeader!: ElementRef;
  public activeTabIndex: number;
  private documentClickListener;
  public menuState: TabMenuState = {
    isOpen: false,
    top: '0px',
    left: '0px',
    item: null,
  };

  ngOnInit() {
    this.documentClickListener = (event: MouseEvent) => {
      if (this.menuState.isOpen) {
        const menu = document.querySelector('.file-explorer-menu');
        const clickedInside = menu?.contains(event.target as Node);
        if (!clickedInside) {
          this.menuState = {
            isOpen: false,
            top: '0px',
            left: '0px',
            item: null,
          };
        }
      }
    };

    document.addEventListener('click', this.documentClickListener);
  }

  getActiveTabIndex() {
    return this.tabs.findIndex(tab => !!tab.active);
  }

  ngOnChanges(changes) {
    if (changes?.tabs?.currentValue?.length) {
      const activeTabIndex = this.getActiveTabIndex();
      if (this.activeTabIndex !== activeTabIndex) {
        this.activeTabIndex = activeTabIndex;
        this.scrollToActiveTab();
      }
    }
  }
  handleSelectTab(tab: Tab<any>) {
    if (this.disableTabSelection) {
      return;
    }
    this.selectedTab.emit(tab);
  }
  handleRemoveTab($event, tab: Tab<any>) {
    $event.preventDefault();
    $event.stopPropagation();
    this.removeTab.emit(tab);
  }

  async scrollToActiveTab() {
    await new Promise(resolve => setTimeout(() => resolve(null), 0.1));
    const tabHeader = this?.tabHeader?.nativeElement;
    if (!tabHeader) return;
    const activeTab = tabHeader?.children?.[this.activeTabIndex];

    if (activeTab) {
      const tabRect = activeTab.getBoundingClientRect();
      const containerRect = tabHeader.getBoundingClientRect();

      // Check if the active tab is out of view
      if (
        tabRect.right > containerRect.right ||
        tabRect.left < containerRect.left
      ) {
        // Scroll the container to bring the active tab into view
        tabHeader.scrollLeft += tabRect.left - containerRect.left;
      }
    }
  }

  handleNewTab($event) {
    $event.preventDefault();
    $event.stopPropagation();
    this.newTab.emit();
  }

  handleToggleMenu(event: MouseEvent, item: any) {
    event.preventDefault();
    event.stopImmediatePropagation();

    if (this.menuState.item === item && this.menuState.isOpen) {
      this.menuState = {
        isOpen: false,
        top: '0px',
        left: '0px',
        item: null,
      };
      return;
    }

    const target = event.currentTarget as HTMLElement;
    const rect = target.getBoundingClientRect();

    this.menuState = {
      isOpen: true,
      top: `${rect.bottom + 5}px`,
      left: `${rect.right + 5}px`,
      item: item,
    };
  }

  handleMenuItemClicked($event, menuItem: TabMenuState) {
    $event.preventDefault();
    $event.stopPropagation();
    this.menuItemClicked.emit({ item: this.menuState.item, menuItem });
    this.menuState = {
      isOpen: false,
      top: '0px',
      left: '0px',
      item: null,
    };
  }
}

export interface TabMenuItem {
  label: string;
  action: string;
  icon?: string;
}

export interface Tab<t> {
  title: string;
  menuItems?: TabMenuItem[];
  id?: number;
  active?: boolean;
  canDelete?: boolean;
  data?: t;
}
